import React, { useState, useEffect } from 'react'
import _ from 'lodash'
import Actions from '../../../../actions'
import { Spinner } from '../../../../components/generic'
import { history } from '../../../../store'
import { useDispatch } from 'react-redux'
import useResidents from './hooks/useResidents'
import { Home, ResidentItem } from '@lmc/ts-common'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { matchesSearch } from './utils'
import { useTranslation } from 'react-i18next'
import {
  ResidentListHeader,
  ResidentListItem,
  ResidentSideFilters,
} from './components'
import { getNominative } from '../../../../common/utils'
import { isBrowser, isTablet } from 'react-device-detect'
import { EmptyState, NoDataIcon } from '@lmc/ui-kit'
import Operations from '../../../../operations'
import useZones from 'hooks/useZones'

const StyledRowWrapper = styled.div`
  display: flex;
  margin-top: 0px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
    max-height: max-content;
    overflow-y: auto;
    overflow-x: hidden;
  }
`

const StyledMainContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: auto;
  margin-right: auto;
  max-width: 2000px;
  height: calc(100vh - 109px);
  margin-left: auto;
  margin-right: auto;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    height: calc(100vh - 63px);
  }
`

const StyledUserInfoContainer = styled.div`
  padding-top: 54px;
  margin: 0 24px;
  height: 100%;
`

const StyledScrollerDesktop = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 160px);
  overflow-y: auto;
  width: 100%;
`

const ResidentList: React.FC = () => {
  const dispatch = useDispatch()

  const [usersFetchInterval, setUsersFetchInterval] = useState<
    ReturnType<typeof setInterval> | undefined
  >()

  const [search, setSearch] = useState<string>('')

  const [zones, setZones] = useState<string[]>([])

  const [remarks, setRemarks] = useState<string[]>([])

  const [statuses, setStatuses] = useState<string[]>(['active', 'dummy'])

  const { isLoading, residents, fetchCarers } = useResidents()

  const features = useSelector(
    (state: { global: { home: Home } }) => state.global.home,
  )

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative,
  )

  const { zones: zonesData, fetchZones } = useZones()

  const { t } = useTranslation()

  useEffect(() => {
    if (features?.pro) {
      fetchZones()
    }
    setUsersFetchInterval(setInterval(fetchCarers, 600000))
    fetchCarers()
    return () => {
      clearInterval(usersFetchInterval)
    }
  }, [])

  const openModal = () => {
    const id = 'addResident'
    dispatch(Actions.openModal({ modalId: id }))
    dispatch(Actions.addForm({ formId: id }))
  }

  const hasResidents = !!residents?.length

  const searchResults = _.filter(residents, (resident) =>
    matchesSearch(resident, statuses, zones, remarks, search),
  )

  if (isLoading) return <Spinner />

  const hasSearchResults = !!searchResults?.length

  const residentList = hasSearchResults ? (
    searchResults.map((resident: ResidentItem, index: number) => (
      <ResidentListItem
        firstItem={index === 0}
        key={resident.id}
        resident={resident}
        onClick={(e: { stopPropagation: () => void }) => {
          e.stopPropagation()
          history.push(`/residents/${resident.id}/profile`)
        }}
      />
    ))
  ) : (
    <EmptyState
      title={t(`general.emptyState.noResidentsListResultTitle`, {
        variable: getNominative(
          preferredPeopleNominative,
          'Resident',
        )?.toLowerCase(),
      })}
      description={t(`general.emptyState.noTeamListResultDescription`)}
      icon={<NoDataIcon />}
    />
  )

  const handleSubscribePro = () => {
    dispatch(
      Operations.openNewPlan({
        planId: 'pro-monthly',
      }),
    )
  }

  return (
    <StyledMainContainer>
      <ResidentListHeader
        openModal={openModal}
        onSearch={setSearch}
        title={getNominative(preferredPeopleNominative, 'Residents')}
        searchPlaceholder={t(`general.team.typeToSearch`)}
        buttonTitle={t(`general.resident.addServiceUser`, {
          variable: getNominative(preferredPeopleNominative, 'Resident'),
        })}
      />
      {hasResidents ? (
        <StyledRowWrapper>
          <ResidentSideFilters
            zonesData={zonesData}
            onZones={setZones}
            onStatuses={setStatuses}
            onRemarks={setRemarks}
            statuses={statuses}
            zones={zones}
            remarks={remarks}
            features={features}
            clearButtonTitle={t(`general.buttons.clear`)}
            statusTitle={t(`general.team.status`)}
            remarksTitle={t(`general.resident.remarks`)}
            zoneTitle={t(`general.team.zone`)}
            residents={residents}
            noZoneTitle={t(`general.team.noZone`)}
            viewProTitle={t(`general.team.viewPro`)}
            onSubscribe={handleSubscribePro}
            onDefaultOpened={isDesktop}
          />

          {isDesktop ? (
            <StyledScrollerDesktop data-cy="residents-list">
              {residentList}
            </StyledScrollerDesktop>
          ) : (
            residentList
          )}
        </StyledRowWrapper>
      ) : (
        <StyledUserInfoContainer>
          <EmptyState
            title={t(`general.emptyState.noServiceUsersResponseTitle`, {
              variable: getNominative(
                preferredPeopleNominative,
                'Residents',
              )?.toLowerCase(),
            })}
            description={t(
              `general.emptyState.noServiceUsersResponseDescription`,
              {
                variable: getNominative(
                  preferredPeopleNominative,
                  'Residents',
                )?.toLowerCase(),
              },
            )}
            icon={<NoDataIcon />}
          />
        </StyledUserInfoContainer>
      )}
    </StyledMainContainer>
  )
}

const isDesktop = isBrowser || isTablet

export default ResidentList
